import React, { useCallback, useState } from 'react';
import { Upload, FolderOpen, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import type { UploadedImage } from '@/pages/ImageToolsHub';

interface EnhancedImageUploadProps {
  images: UploadedImage[];
  setImages: React.Dispatch<React.SetStateAction<UploadedImage[]>>;
}

export const EnhancedImageUpload: React.FC<EnhancedImageUploadProps> = ({
  images,
  setImages,
}) => {
  const [sortBy, setSortBy] = useState<'name' | 'size' | 'format'>('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      toast.error('No valid image files selected');
      return;
    }

    if (images.length + imageFiles.length > 50) {
      toast.error('Maximum 50 images allowed');
      return;
    }

    const newImages: UploadedImage[] = await Promise.all(
      imageFiles.map(async (file) => {
        const preview = URL.createObjectURL(file);
        const dimensions = await getImageDimensions(file);
        return {
          id: Math.random().toString(36).substring(7),
          file,
          preview,
          name: file.name,
          size: file.size,
          dimensions,
          format: file.type.split('/')[1].toUpperCase(),
          selected: false,
        };
      })
    );

    setImages(prev => [...prev, ...newImages]);
    toast.success(`Added ${newImages.length} image${newImages.length > 1 ? 's' : ''}`);
  }, [images.length, setImages]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const removeImage = (id: string) => {
    setImages(prev => {
      const image = prev.find(img => img.id === id);
      if (image) URL.revokeObjectURL(image.preview);
      return prev.filter(img => img.id !== id);
    });
  };

  const toggleSelect = (id: string) => {
    setImages(prev =>
      prev.map(img => img.id === id ? { ...img, selected: !img.selected } : img)
    );
  };

  const selectAll = () => {
    setImages(prev => prev.map(img => ({ ...img, selected: true })));
  };

  const deselectAll = () => {
    setImages(prev => prev.map(img => ({ ...img, selected: false })));
  };

  const removeSelected = () => {
    setImages(prev => {
      const toRemove = prev.filter(img => img.selected);
      toRemove.forEach(img => URL.revokeObjectURL(img.preview));
      return prev.filter(img => !img.selected);
    });
    toast.success('Removed selected images');
  };

  const sortedAndFilteredImages = [...images]
    .filter(img => img.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'size') return b.size - a.size;
      if (sortBy === 'format') return a.format.localeCompare(b.format);
      return 0;
    });

  const selectedCount = images.filter(img => img.selected).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload Images</CardTitle>
          <CardDescription>
            Upload up to 50 images. Supports JPG, PNG, WEBP, and more.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
            }`}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium mb-2">
              Drag and drop images here
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse
            </p>
            <div className="flex gap-2 justify-center">
              <Button asChild variant="default">
                <label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFiles(e.target.files)}
                    className="hidden"
                  />
                  Select Files
                </label>
              </Button>
              <Button asChild variant="outline">
                <label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    // @ts-ignore
                    webkitdirectory=""
                    directory=""
                    onChange={(e) => handleFiles(e.target.files)}
                    className="hidden"
                  />
                  <FolderOpen className="h-4 w-4 mr-2" />
                  Select Folder
                </label>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {images.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Uploaded Images ({images.length}/50)</CardTitle>
                {selectedCount > 0 && (
                  <CardDescription>
                    {selectedCount} image{selectedCount > 1 ? 's' : ''} selected
                  </CardDescription>
                )}
              </div>
              <div className="flex gap-2">
                {selectedCount > 0 && (
                  <>
                    <Button size="sm" variant="outline" onClick={deselectAll}>
                      Deselect All
                    </Button>
                    <Button size="sm" variant="destructive" onClick={removeSelected}>
                      Remove Selected
                    </Button>
                  </>
                )}
                {selectedCount === 0 && images.length > 0 && (
                  <Button size="sm" variant="outline" onClick={selectAll}>
                    Select All
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search images..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Sort by Name</SelectItem>
                  <SelectItem value="size">Sort by Size</SelectItem>
                  <SelectItem value="format">Sort by Format</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {sortedAndFilteredImages.map((image) => (
                <div
                  key={image.id}
                  className={`relative group rounded-lg overflow-hidden border-2 transition-all ${
                    image.selected ? 'border-primary ring-2 ring-primary/20' : 'border-border'
                  }`}
                >
                  <div
                    className="cursor-pointer"
                    onClick={() => toggleSelect(image.id)}
                  >
                    <img
                      src={image.preview}
                      alt={image.name}
                      className="w-full aspect-square object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={image.selected}
                        onChange={() => toggleSelect(image.id)}
                        className="opacity-0 group-hover:opacity-100 w-5 h-5"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <Button
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100"
                    onClick={() => removeImage(image.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <div className="p-2 bg-card">
                    <p className="text-xs font-medium truncate">{image.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {image.format} • {(image.size / 1024).toFixed(1)}KB
                    </p>
                    {image.dimensions && (
                      <p className="text-xs text-muted-foreground">
                        {image.dimensions.width} × {image.dimensions.height}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
